import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Plus, Send, Trash2, CheckCircle, XCircle, Clock,
  Euro, FileText, ArrowLeft, Loader2, ChevronDown, ChevronUp
} from "lucide-react";
import PageHeader from '../components/PageHeader';
import PageLoader from '../components/PageLoader';
import EmptyState from '../components/EmptyState';
import StatusBadge from '../components/StatusBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;


const CABIN_CLASSES = ["Economy", "Premium Economy", "Business", "First"];
const BAGGAGE_OPTIONS = ["No baggage", "Hand carry only", "23kg checked", "2x23kg checked", "30kg checked"];

const emptyForm = {
  customer_name: "", customer_email: "", customer_phone: "",
  flight_description: "", airline: "", cabin_class: "Economy",
  baggage: "23kg checked", selling_price: "", base_price: "",
  valid_until: "", notes: "",
  segments: [{ departure_airport: "", arrival_airport: "", departure_date: "" }],
};

export default function Quotes() {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => { fetchQuotes(); }, []);

  const fetchQuotes = async () => {
    try {
      const res = await axios.get(`${API}/quotes`, { headers: getAuthHeaders() });
      setQuotes(res.data);
    } catch (e) { toast.error("Failed to load quotes"); }
    finally { setLoading(false); }
  };

  const addSegment = () => setForm(p => ({
    ...p, segments: [...p.segments, { departure_airport: "", arrival_airport: "", departure_date: "" }]
  }));

  const removeSegment = (i) => setForm(p => ({
    ...p, segments: p.segments.filter((_, idx) => idx !== i)
  }));

  const updateSegment = (i, field, value) => setForm(p => {
    const segs = [...p.segments];
    segs[i] = { ...segs[i], [field]: value };
    return { ...p, segments: segs };
  });

  const sendQuote = async () => {
    if (!form.customer_name || !form.customer_email) { toast.error("Customer name and email required"); return; }
    if (!form.flight_description) { toast.error("Flight description required"); return; }
    if (!form.selling_price) { toast.error("Price required"); return; }
    setSending(true);
    try {
      const payload = {
        ...form,
        selling_price: parseFloat(form.selling_price),
        base_price: parseFloat(form.base_price) || 0,
        segments: form.segments.filter(s => s.departure_airport && s.arrival_airport),
      };
      await axios.post(`${API}/quotes`, payload, { headers: getAuthHeaders() });
      toast.success("Quote sent to customer!");
      setShowForm(false);
      setForm(emptyForm);
      fetchQuotes();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send quote");
    } finally { setSending(false); }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/quotes/${id}/status`, { status }, { headers: getAuthHeaders() });
      toast.success("Status updated");
      fetchQuotes();
    } catch (e) { toast.error("Failed"); }
  };

  const deleteQuote = async (id) => {
    if (!window.confirm("Delete this quote?")) return;
    try {
      await axios.delete(`${API}/quotes/${id}`, { headers: getAuthHeaders() });
      toast.success("Deleted");
      fetchQuotes();
    } catch (e) { toast.error("Failed"); }
  };

  const profit = (parseFloat(form.selling_price) || 0) - (parseFloat(form.base_price) || 0);

  return (
    <DashboardLayout>
      <div className="p-6 max-w-4xl mx-auto">
        {/* Header */}
        <PageHeader
          title="Deals & Quotes"
          subtitle="Send price quotes to customers before booking"
          actions={
            <Button onClick={() => setShowForm(!showForm)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white">
              <Plus className="w-4 h-4 mr-2" />
              New Quote
            </Button>
          }
        />

        {/* New Quote Form */}
        {showForm && (
          <Card className="mb-6 border-indigo-200 bg-indigo-50">
            <CardHeader className="pb-3">
              <CardTitle className="text-indigo-800 text-base flex items-center gap-2">
                <FileText className="w-4 h-4" /> New Price Quote
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">

              {/* Customer */}
              <div className="bg-white rounded-lg p-4 space-y-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Customer</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Full Name *</Label>
                    <Input value={form.customer_name}
                      onChange={e => setForm(p => ({ ...p, customer_name: e.target.value }))}
                      placeholder="Ahmed Khan" className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs">Email *</Label>
                    <Input type="email" value={form.customer_email}
                      onChange={e => setForm(p => ({ ...p, customer_email: e.target.value }))}
                      placeholder="ahmed@example.com" className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs">Phone</Label>
                    <Input value={form.customer_phone}
                      onChange={e => setForm(p => ({ ...p, customer_phone: e.target.value }))}
                      placeholder="+31 6 12345678" className="mt-1" />
                  </div>
                </div>
              </div>

              {/* Flight */}
              <div className="bg-white rounded-lg p-4 space-y-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Flight Details</p>
                <div>
                  <Label className="text-xs">Flight Description * (shown to customer)</Label>
                  <Input value={form.flight_description}
                    onChange={e => setForm(p => ({ ...p, flight_description: e.target.value }))}
                    placeholder="AMS → KHI via DXB, 15 Apr 2026, Return 30 Apr 2026"
                    className="mt-1" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Airline</Label>
                    <Input value={form.airline}
                      onChange={e => setForm(p => ({ ...p, airline: e.target.value }))}
                      placeholder="Emirates" className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs">Class</Label>
                    <select className="w-full mt-1 border rounded-md px-3 py-2 text-sm bg-white"
                      value={form.cabin_class}
                      onChange={e => setForm(p => ({ ...p, cabin_class: e.target.value }))}>
                      {CABIN_CLASSES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs">Baggage</Label>
                    <select className="w-full mt-1 border rounded-md px-3 py-2 text-sm bg-white"
                      value={form.baggage}
                      onChange={e => setForm(p => ({ ...p, baggage: e.target.value }))}>
                      {BAGGAGE_OPTIONS.map(b => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </div>
                </div>

                {/* Segments */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-xs">Flight Segments</Label>
                    <button onClick={addSegment} className="text-xs text-indigo-600 hover:underline">
                      + Add segment
                    </button>
                  </div>
                  {form.segments.map((seg, i) => (
                    <div key={i} className="grid grid-cols-7 gap-2 mb-2 items-center">
                      <Input value={seg.departure_airport}
                        onChange={e => updateSegment(i, "departure_airport", e.target.value.toUpperCase())}
                        placeholder="AMS" maxLength={3}
                        className="col-span-2 font-mono text-center text-sm" />
                      <span className="text-center text-gray-400 text-sm">→</span>
                      <Input value={seg.arrival_airport}
                        onChange={e => updateSegment(i, "arrival_airport", e.target.value.toUpperCase())}
                        placeholder="KHI" maxLength={3}
                        className="col-span-2 font-mono text-center text-sm" />
                      <Input type="date" value={seg.departure_date}
                        onChange={e => updateSegment(i, "departure_date", e.target.value)}
                        className="col-span-1 text-xs" />
                      {form.segments.length > 1 && (
                        <button onClick={() => removeSegment(i)} className="text-red-400 hover:text-red-600 text-xs">✕</button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Pricing */}
              <div className="bg-white rounded-lg p-4 space-y-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Pricing</p>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Your Cost (€) — not shown to customer</Label>
                    <Input type="number" step="0.01" value={form.base_price}
                      onChange={e => setForm(p => ({ ...p, base_price: e.target.value }))}
                      placeholder="0.00" className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs">Quote Price (€) * — shown to customer</Label>
                    <Input type="number" step="0.01" value={form.selling_price}
                      onChange={e => setForm(p => ({ ...p, selling_price: e.target.value }))}
                      placeholder="0.00" className="mt-1" />
                  </div>
                  <div className={`p-3 rounded-lg border ${profit >= 0 ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
                    <p className="text-xs text-gray-500">Your Profit</p>
                    <p className={`text-xl font-bold ${profit >= 0 ? "text-green-700" : "text-red-600"}`}>
                      €{profit.toFixed(2)}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Valid Until</Label>
                    <Input type="date" value={form.valid_until}
                      onChange={e => setForm(p => ({ ...p, valid_until: e.target.value }))}
                      className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs">Notes (shown to customer)</Label>
                    <Input value={form.notes}
                      onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                      placeholder="Price includes all taxes and fees"
                      className="mt-1" />
                  </div>
                </div>
              </div>

              <div className="flex gap-3">
                <Button onClick={sendQuote} disabled={sending}
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white">
                  {sending
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
                    : <><Send className="w-4 h-4 mr-2" />Send Quote to Customer</>}
                </Button>
                <Button variant="outline" onClick={() => { setShowForm(false); setForm(emptyForm); }}>
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quotes List */}
        {loading ? (
          <PageLoader inline message="Loading quotes…" />
        ) : quotes.length === 0 ? (
          <EmptyState
            title="No quotes yet"
            description="Create a new quote to get started."
          />
        ) : (
          <div className="space-y-3">
            {quotes.map(q => (
              <Card key={q.id} className="border-gray-200 hover:border-indigo-200 transition-colors">
                <CardContent className="py-3 px-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-sm font-bold text-indigo-700">{q.quote_number}</span>
                        <span className="font-medium text-gray-800">{q.customer_name}</span>
                        <StatusBadge status={q.status} />
                        {q.email_sent && <Badge className="text-xs bg-green-100 text-green-600">✉ sent</Badge>}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5 truncate">{q.flight_description}</p>
                      <p className="text-xs text-gray-400">{q.customer_email} · {q.created_at?.slice(0, 10)}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-bold text-lg text-gray-900">€{Number(q.selling_price).toFixed(2)}</p>
                      {q.base_price > 0 && (
                        <p className="text-xs text-green-600">+€{(q.selling_price - q.base_price).toFixed(2)} profit</p>
                      )}
                    </div>
                  </div>

                  {/* Expanded actions */}
                  <div className="flex items-center gap-3 mt-2 pt-2 border-t border-gray-100">
                    <button onClick={() => setExpandedId(expandedId === q.id ? null : q.id)}
                      className="text-xs text-indigo-600 hover:underline flex items-center gap-0.5">
                      Actions {expandedId === q.id ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    </button>
                    {q.valid_until && (
                      <span className="text-xs text-gray-400">Valid until {q.valid_until}</span>
                    )}
                    <button onClick={() => deleteQuote(q.id)} className="ml-auto text-red-400 hover:text-red-600">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  {expandedId === q.id && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {["sent", "accepted", "rejected", "expired", "booked"].map(s => (
                        <button key={s} onClick={() => updateStatus(q.id, s)}
                          className={`text-xs px-2 py-1 rounded border transition-colors ${
                            q.status === s
                              ? "bg-indigo-600 text-white border-indigo-600"
                              : "border-gray-200 text-gray-600 hover:border-indigo-300"
                          }`}>
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
